import {
  FacebookIntegrationView,
  GitHubInstallationRequestView,
  GitHubIntegrationView,
  GoogleIntegrationStatus,
  InstagramIntegrationView,
  MessagingPlatform,
  MicrosoftIntegrationStatus,
  SlackTeamIntegrationView,
  SlackUserIntegrationView,
  WhatsAppIntegrationView,
} from '@this+that/models';
import { StoreApi } from 'zustand';

import {
  buildRemovedEmailAccountKey,
  pruneExpiredRemovedEmailAccounts,
  isEmailAccountRemoved as resolveIsEmailAccountRemoved,
} from './removedEmailAccounts';
import { IntegrationsState } from './types';
import { IntegrationAccount } from '../../../models/integrationAccount';
import { REMOVED_EMAIL_ACCOUNT_TTL_MS } from '../../../utils/constants/integrationConstants';
import { EMAIL_PLATFORM_SET } from '../UserCommunications/connectedAccountRows';

type ZustandSet = StoreApi<IntegrationsState>['setState'];
type ZustandGet = StoreApi<IntegrationsState>['getState'];

export const handleSetLoading = (set: ZustandSet, isLoading: boolean) => {
  set((state) => (state.isLoading === isLoading ? state : { isLoading }));
};

export const handleSetCurrentIntegrationAccount = (
  set: ZustandSet,
  integrationAccount: IntegrationAccount,
) => {
  set(() => ({
    currentIntegrationAccount: integrationAccount,
  }));
};

export const handleRemoveIntegrationAccount = (
  set: ZustandSet,
  integrationAccount: Pick<IntegrationAccount, 'platform' | 'identifier'>,
) => {
  set((state) => {
    const filteredAccounts = state.integrationAccounts.filter(
      (account) => account.identifier !== integrationAccount.identifier,
    );

    const currentIntegrationAccount =
      state.currentIntegrationAccount?.identifier ===
      integrationAccount.identifier
        ? filteredAccounts[0] || null
        : state.currentIntegrationAccount;

    return {
      ...state,
      integrationAccounts: filteredAccounts,
      currentIntegrationAccount,
    };
  });
};

const resolveCurrentIntegrationAccount = (
  accounts: IntegrationAccount[],
  currentIdentifier: string | undefined,
): IntegrationAccount | null =>
  accounts.find((account) => account.identifier === currentIdentifier) ??
  accounts[0] ??
  null;

// A reconnect (same platform + address seen again) clears the blocklist entry
// rather than waiting out the TTL — never derived from an accounts diff, only
// from the accounts this call is actually handed.
const clearReconnectedEmailAccounts = (
  removedEmailAccounts: Record<string, number>,
  accounts: IntegrationAccount[],
): Record<string, number> => {
  const reconnectedKeys = new Set(
    accounts
      .filter((account) => EMAIL_PLATFORM_SET.has(account.platform))
      .map((account) =>
        buildRemovedEmailAccountKey(account.platform, account.displayName),
      ),
  );

  if (reconnectedKeys.size === 0) {
    return removedEmailAccounts;
  }

  return Object.fromEntries(
    Object.entries(removedEmailAccounts).filter(
      ([key]) => !reconnectedKeys.has(key),
    ),
  );
};

export const handleSetIntegrationAccounts = (
  set: ZustandSet,
  accounts: IntegrationAccount[],
) => {
  set((state) => ({
    integrationAccounts: accounts,
    currentIntegrationAccount: resolveCurrentIntegrationAccount(
      accounts,
      state.currentIntegrationAccount?.identifier,
    ),
    removedEmailAccounts: clearReconnectedEmailAccounts(
      state.removedEmailAccounts,
      accounts,
    ),
  }));
};

export const handleMarkEmailAccountRemoved = (
  set: ZustandSet,
  platform: MessagingPlatform,
  address: string,
) => {
  if (!EMAIL_PLATFORM_SET.has(platform)) {
    return;
  }

  set((state) => {
    const now = Date.now();
    const pruned = pruneExpiredRemovedEmailAccounts(
      state.removedEmailAccounts,
      now,
    );

    return {
      removedEmailAccounts: {
        ...pruned,
        [buildRemovedEmailAccountKey(platform, address)]:
          now + REMOVED_EMAIL_ACCOUNT_TTL_MS,
      },
    };
  });
};

export const handleIsEmailAccountRemoved = (
  get: ZustandGet,
  platform: MessagingPlatform,
  address: string,
): boolean =>
  resolveIsEmailAccountRemoved(
    get().removedEmailAccounts,
    platform,
    address,
    Date.now(),
  );

export const handleSetUserSlackIntegrations = (
  set: ZustandSet,
  integrations: SlackUserIntegrationView[],
) => {
  set({ userSlackIntegrations: integrations });
};

export const handleSetTeamSlackBotIntegrations = (
  set: ZustandSet,
  integrations: SlackTeamIntegrationView[],
) => {
  set({ teamSlackBotIntegrations: integrations });
};

export const handleSetUserMicrosoftIntegrations = (
  set: ZustandSet,
  integrations: MicrosoftIntegrationStatus[],
) => {
  set({ userMicrosoftIntegrations: integrations });
};

export const handleSetTeamInstagramIntegrations = (
  set: ZustandSet,
  integrations: InstagramIntegrationView[],
) => {
  set({ teamInstagramIntegrations: integrations });
};

export const handleSetTeamFacebookIntegrations = (
  set: ZustandSet,
  integrations: FacebookIntegrationView[],
) => {
  set({ teamFacebookIntegrations: integrations });
};

export const handleSetTeamGithubIntegrations = (
  set: ZustandSet,
  integrations: GitHubIntegrationView[],
) => {
  set({ teamGithubIntegrations: integrations });
};

export const handleSetTeamGithubInstallationRequests = (
  set: ZustandSet,
  requests: GitHubInstallationRequestView[],
) => {
  set({ teamGithubInstallationRequests: requests });
};

export const handleClearTeamScopedIntegrations = (set: ZustandSet) => {
  set({
    teamSlackBotIntegrations: [],
    teamInstagramIntegrations: [],
    teamFacebookIntegrations: [],
    teamGithubIntegrations: [],
    teamGithubInstallationRequests: [],
  });
};

export const handleSetUserWhatsAppIntegrations = (
  set: ZustandSet,
  integrations: WhatsAppIntegrationView[],
) => {
  set({ userWhatsAppIntegrations: integrations });
};

export const handleSetUserGoogleIntegrations = (
  set: ZustandSet,
  integrations: GoogleIntegrationStatus[],
) => {
  set({ userGoogleIntegrations: integrations });
};

export const handleSetIsDraftSaving = (
  set: ZustandSet,
  isDraftSaving: boolean,
) => {
  set({ isDraftSaving });
};

export const handleSetCurrentEmailFilters = (
  set: ZustandSet,
  filters: string[] | undefined,
) => {
  set({ currentEmailFilters: filters });
};

export const handleResetStore = (set: ZustandSet) => {
  set({
    isLoading: false,
    integrationAccounts: [],
    currentIntegrationAccount: null,
    userSlackIntegrations: [],
    teamSlackBotIntegrations: [],
    userMicrosoftIntegrations: [],
    userWhatsAppIntegrations: [],
    teamInstagramIntegrations: [],
    teamFacebookIntegrations: [],
    teamGithubIntegrations: [],
    teamGithubInstallationRequests: [],
    userGoogleIntegrations: [],
    isDraftSaving: false,
    currentEmailFilters: undefined,
    removedEmailAccounts: {},
  });
};
